import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, MapPin, Building2, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  planning: "bg-sky-100 text-sky-800 border-sky-200",
  on_hold: "bg-amber-100 text-amber-800 border-amber-200",
  completed: "bg-neutral-100 text-neutral-700 border-neutral-200",
};

const empty = {
  name: "", client: "", address: "", status: "active",
  start_date: "", end_date: "", progress: 0, budget: 0, description: "",
};

export default function ProjectsPage() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [snagCounts, setSnagCounts] = useState({}); // project_id -> { critical, open }
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: s }] = await Promise.all([
        api.get(`/projects?lang=${lang}`),
        api.get(`/snags?lang=${lang}`),
      ]);
      setProjects(p);
      const counts = {};
      for (const sn of s) {
        const c = counts[sn.project_id] || { critical: 0, open: 0 };
        if (sn.priority === "critical") c.critical += 1;
        if (sn.status === "open") c.open += 1;
        counts[sn.project_id] = c;
      }
      setSnagCounts(counts);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [lang]);

  const openNew = () => { setForm(empty); setEditId(null); setDialogOpen(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name || "", client: p.client || "", address: p.address || "",
      status: p.status, start_date: p.start_date || "", end_date: p.end_date || "",
      progress: p.progress || 0, budget: p.budget || 0, description: p.description || "",
    });
    setEditId(p.id);
    setDialogOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, progress: Number(form.progress), budget: Number(form.budget) };
      if (editId) await api.patch(`/projects/${editId}`, payload);
      else await api.post("/projects", payload);
      setDialogOpen(false);
      await load();
      toast.success(editId ? t("project_updated") : t("project_created"));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        toast.error(apiErr(e), {
          action: { label: "Upgrade", onClick: () => nav("/app/billing") },
          duration: 8000,
        });
      } else {
        toast.error(apiErr(e));
      }
    }
  };

  const remove = async (id) => {
    if (!confirm(t("delete_project_confirm"))) return;
    try { await api.delete(`/projects/${id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="label-eyebrow">{t("dashboard")}</p>
          <h1 className="font-[Fraunces] text-4xl font-bold tracking-tight text-neutral-950 mt-1">
            {t("projects")}
          </h1>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-[#B8933A] hover:bg-[#8C6D22]" data-testid="new-project-button">
              <Plus className="w-4 h-4 mr-1" /> {t("new_project")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-[Fraunces]">{editId ? t("edit") : t("new_project")}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label className="label-eyebrow">{t("project_name")}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="label-eyebrow">{t("client")}</Label>
                <Input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} data-testid="project-client-input" />
              </div>
              <div className="space-y-2">
                <Label className="label-eyebrow">{t("status")}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planning">{t("planning")}</SelectItem>
                    <SelectItem value="active">{t("in_progress")}</SelectItem>
                    <SelectItem value="on_hold">{t("on_hold")}</SelectItem>
                    <SelectItem value="completed">{t("completed")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="label-eyebrow">{t("address")}</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="project-address-input" />
              </div>
              <div className="space-y-2">
                <Label className="label-eyebrow">{t("start_date")}</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="label-eyebrow">{t("end_date")}</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="label-eyebrow">{t("progress")} (%)</Label>
                <Input type="number" min="0" max="100" value={form.progress}
                  onChange={(e) => setForm({ ...form, progress: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="label-eyebrow">{t("budget")}</Label>
                <Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="label-eyebrow">{t("description")}</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("cancel")}</Button>
              <Button onClick={save} className="bg-[#B8933A] hover:bg-[#8C6D22]" data-testid="save-project-button">{t("save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-neutral-400 text-sm">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="surface-card p-12 text-center">
          <p className="text-neutral-500">{t("no_projects")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {projects.map((p) => {
            const c = snagCounts[p.id] || { critical: 0, open: 0 };
            return (
            <div key={p.id} data-testid={`project-card-${p.id}`}
              className="surface-card p-6 transition-colors duration-200 hover:border-neutral-400 group cursor-pointer"
              onClick={() => nav(`/app/projects/${p.id}`)}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`border ${STATUS_COLORS[p.status] || ""}`}>{t(p.status)}</Badge>
                  {c.critical > 0 && (
                    <Badge className="border bg-red-100 text-red-800 border-red-200" data-testid={`critical-badge-${p.id}`}>
                      <AlertTriangle className="w-3 h-3 mr-1" />{c.critical} {t("critical")}
                    </Badge>
                  )}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(p.id); }}
                  className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-600 transition-colors duration-200"
                  data-testid={`delete-project-${p.id}`}
                  aria-label="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <h3 className="mt-4 font-[Fraunces] font-semibold text-xl text-neutral-950">{p.name}</h3>
              {p.client && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-neutral-500">
                  <Building2 className="w-3.5 h-3.5" /> {p.client}
                </div>
              )}
              {p.address && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-neutral-500">
                  <MapPin className="w-3.5 h-3.5" /> {p.address}
                </div>
              )}
              <div className="mt-5">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="label-eyebrow">{t("progress")}</span>
                  <span className="font-mono text-neutral-700">{p.progress}%</span>
                </div>
                <Progress value={p.progress} className="h-1.5" />
              </div>
              <div className="mt-4 flex items-center justify-between text-xs text-neutral-500">
                <span>{p.start_date || "—"} → {p.end_date || "—"}</span>
                <span className="flex items-center gap-1 text-[#B8933A] font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {t("open_project")} <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}
